import { Link } from "react-router-dom";
import { Train, Car, Bike, SquareParking } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=47.0705647,8.2848735";

const IconFrame = ({ children }) => (
    <div className="shrink-0 w-12 h-12 border border-[#c9a24b]/40 flex items-center justify-center text-[#c9a24b]" aria-hidden="true">
        {children}
    </div>
);

const Divider = () => <div className="h-px bg-[#c9a24b]/20" aria-hidden="true" />;

export const Localizacao = () => {
    const { t } = useLanguage();
    const L = t.localizacao;
    const access = [
        { id: "transportes", icon: <Train size={22} strokeWidth={1.3} />, title: L.transportesTitulo, text: L.transportes },
        { id: "carro", icon: <Car size={22} strokeWidth={1.3} />, title: L.carroTitulo, text: L.carro },
        { id: "bicicleta", icon: <Bike size={22} strokeWidth={1.3} />, title: L.bicicletaTitulo, text: L.bicicleta },
    ];

    return (
        <section id="localizacao" className="relative overflow-hidden min-h-screen flex flex-col" data-testid="localizacao-section">
            {/* Fotografia noturna do Centro Papa Giovanni em fundo total, com degradés para a informação se fundir na imagem */}
            <div className="absolute inset-0" aria-hidden="true">
                <img loading="lazy" decoding="async" src="/images/local-noite.webp" alt="" className="w-full h-full object-cover object-center" data-testid="localizacao-bg" />
                <div className="absolute inset-0 bg-[#070503]/55" />
                <div className="absolute inset-x-0 top-0 h-48 sm:h-72 bg-gradient-to-b from-[#070503] via-[#070503]/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-48 sm:h-72 bg-gradient-to-t from-[#070503] via-[#070503]/70 to-transparent" />
                <div className="absolute inset-y-0 left-0 w-full lg:w-[75%] bg-gradient-to-r from-[#070503]/95 via-[#070503]/60 to-transparent" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1200px] w-full px-5 sm:px-10 py-12 sm:py-16 my-auto">
                <Reveal>
                    <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="localizacao-label">{L.label}</p>
                    <h2 className="font-display font-medium gold-text text-3xl sm:text-4xl lg:text-5xl tracking-[0.06em] mt-3" data-testid="localizacao-titulo">
                        {L.titulo}
                    </h2>
                    <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90 mt-4" />
                    <p className="text-base sm:text-lg font-light text-[#efe6d6]/80 leading-relaxed max-w-2xl mt-4" data-testid="localizacao-intro">
                        {L.intro}
                    </p>
                </Reveal>

                <div className="mt-8 grid lg:grid-cols-2 gap-8 lg:gap-12">
                    <Reveal>
                        <div className="space-y-4 border border-[#c9a24b]/20 bg-[#070503]/60 backdrop-blur-sm px-6 py-5 sm:px-7 sm:py-6" data-testid="localizacao-info-painel">
                            <div data-testid="localizacao-morada">
                                <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#c9a24b]/80">{L.moradaLabel}</p>
                                <p className="font-display font-light text-lg text-[#efe6d6]/90 mt-1.5">{L.moradaValor}</p>
                            </div>
                            <Divider />
                            <div data-testid="localizacao-gps">
                                <p className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#c9a24b]/80">{L.gpsLabel}</p>
                                <p className="font-display font-light text-lg text-[#efe6d6]/90 mt-1.5 tracking-[0.04em]">{L.gpsValor}</p>
                            </div>
                            <Divider />
                            <div className="flex gap-5 items-start" data-testid="localizacao-estacionamento">
                                <IconFrame><SquareParking size={22} strokeWidth={1.3} /></IconFrame>
                                <div>
                                    <h3 className="font-caps text-xs tracking-[0.25em] uppercase text-[#efe6d6]">{L.estacionamentoTitulo}</h3>
                                    <p className="text-sm font-light text-[#efe6d6]/75 leading-relaxed mt-1.5">{L.estacionamento}</p>
                                    <a
                                        href="https://www.google.com/maps/search/?api=1&query=47.0698242,8.2872323"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="localizacao-parking-maps"
                                        className="inline-block mt-2 font-caps text-[10px] tracking-[0.25em] uppercase text-[#c9a24b] underline underline-offset-4 hover:text-[#ecd49b] transition-colors duration-300"
                                    >
                                        {t.local.estacionamentoHager.mapsCta}
                                    </a>
                                </div>
                            </div>
                            <Divider />
                            <a
                                href={MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="localizacao-maps-cta"
                                className="flex items-center gap-5 border border-[#c9a24b]/40 bg-[#070503]/60 backdrop-blur-sm px-5 py-4 hover:border-[#c9a24b] hover:bg-[#c9a24b]/10 transition-colors duration-400"
                            >
                                <img loading="lazy" decoding="async" src="/images/qr-local.webp" alt="QR code — Google Maps" className="w-14 h-14 shrink-0" />
                                <p className="font-caps text-[11px] tracking-[0.25em] uppercase text-[#c9a24b]">{t.local.mapsCta}</p>
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12}>
                        <div className="border border-[#c9a24b]/20 bg-[#070503]/55 backdrop-blur-sm px-6 py-4 sm:px-7 sm:py-5" data-testid="localizacao-acessos-painel">
                            <p className="font-caps text-[11px] tracking-[0.35em] uppercase text-[#c9a24b]" data-testid="localizacao-acessos-titulo">{L.acessosTitulo}</p>
                            <div className="mt-1 divide-y divide-[#c9a24b]/15">
                                {access.map((a) => (
                                    <div key={a.id} className="flex gap-5 items-start py-4 first:pt-3" data-testid={`localizacao-acesso-${a.id}`}>
                                        <IconFrame>{a.icon}</IconFrame>
                                        <div>
                                            <h3 className="font-caps text-xs tracking-[0.25em] uppercase text-[#efe6d6]">{a.title}</h3>
                                            <p className="text-sm font-light text-[#efe6d6]/75 leading-relaxed mt-1.5">{a.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link
                                to="/local"
                                data-testid="local-saber-mais-button"
                                className="inline-block mt-4 font-caps text-[11px] tracking-[0.3em] uppercase text-[#c9a24b] border border-[#c9a24b]/60 px-8 py-3 hover:bg-[#c9a24b] hover:text-[#070503] transition-colors duration-400"
                            >
                                {L.saberMais}
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
